import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cameFrom, getNavIndex, stayedWithin, LOAD_ID } from '../utils/routeTrail';

/**
 * "Put me back where I was" for list pages (Offers, Offer History): the tab
 * and scroll position are saved the moment the user taps into an item, and
 * restored when they come STRAIGHT back from it.
 *
 * Why it is shaped like this (2026-09-29 review of the first version):
 * - "Coming back" means: every page visited since the tap was one of the
 *   offer pages (OFFER_FLOW_PREFIXES), within the same page load. Drill-down
 *   chains restore (Offers -> Offer History -> an offer -> back -> back); a
 *   detour through any other page, or a reload, is a fresh visit. The detail
 *   pages' Back buttons are links, not history.back(), so this is tracked by
 *   utils/routeTrail.js rather than the browser back stack. Sending a new
 *   offer clears the saved spots (clearOfferReturnViews) so you land fresh.
 * - One-shot: read purely at mount (dev StrictMode runs useState initializers
 *   twice), then deleted in a mount effect, whether or not it was used.
 * - Anchored, not pixel-only: it remembers WHICH item was at the top of the
 *   screen (elements marked data-restore-anchor) and its offset, so lists or
 *   buttons that load in above it can't shift where the user lands. Raw
 *   scrollY is the fallback when the anchor is gone.
 * - Restores only when the caller says its lists are ready, then re-asserts
 *   briefly (a single scrollTo is unreliable in the iOS WebView — see
 *   utils/scrollPageToTop.js), and stops the instant the user touches,
 *   scrolls, or types, so it never fights them. Cancelled on unmount.
 * - A navigation can opt out of the scroll (keeping the tab) with
 *   location.state.skipViewRestore — used by flows that deliberately land the
 *   user at the top (passing on / closing an interest).
 */

// The offer pages, as ONE shared set (Offers and Offer History used to keep
// separate copies that could drift apart).
export const OFFER_FLOW_PREFIXES = ['/trade-center', '/offer-history', '/offer/', '/purchase-offer-details/', '/interest/', '/direct-offer'];
export const OFFERS_VIEW_KEY = 'tradeCenter_viewState';
export const OFFER_HISTORY_VIEW_KEY = 'offerHistory_viewState';

// The accepted/completed trades and purchases lists, each pair as ONE flow
// for the same reason the offer pages share a set: the two lists cross-link,
// so a chain like Completed -> Accepted -> a trade -> back -> back must
// restore each page. Separate storage keys keep each list's own spot
// (2026-10-02, after the Offers restore shipped and these pages still reset
// to the top on swipe-back).
export const TRADES_FLOW_PREFIXES = ['/accepted-trades', '/completed-trades', '/trade-progress/', '/trade-confirm/', '/trade-messages/', '/archived-listing/'];
export const PURCHASES_FLOW_PREFIXES = ['/accepted-purchases', '/completed-purchases', '/purchase-progress/', '/purchase-confirm/', '/seller-purchase-confirm/', '/purchase-messages/', '/archived-listing/'];
export const ACCEPTED_TRADES_VIEW_KEY = 'acceptedTrades_viewState';
export const COMPLETED_TRADES_VIEW_KEY = 'completedTrades_viewState';
export const ACCEPTED_PURCHASES_VIEW_KEY = 'acceptedPurchases_viewState';
export const COMPLETED_PURCHASES_VIEW_KEY = 'completedPurchases_viewState';

/**
 * Forget both pages' saved spots. Called after a new offer is sent: the
 * user's context has changed (their new offer is what matters), so they
 * should land fresh instead of back at a row that may no longer exist.
 */
export function clearOfferReturnViews() {
  try {
    sessionStorage.removeItem(OFFERS_VIEW_KEY);
    sessionStorage.removeItem(OFFER_HISTORY_VIEW_KEY);
  } catch (err) { /* fine */ }
}

const MAX_AGE_MS = 30 * 60 * 1000;
const REASSERT_DELAYS_MS = [0, 120, 300, 600, 1000, 1500];
// Any deliberate input from the user ends the restore immediately.
const USER_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
const LISTENER_OPTS = { passive: true };
// A trackpad back-swipe keeps emitting (mostly horizontal) wheel events for a
// moment AFTER the route changes; counting those as "the user took over"
// cancelled the restore and left the page too high (found on localhost,
// 2026-09-29). Ignore horizontal wheel motion outright, and any input in the
// first moment after arriving.
const ARRIVAL_GRACE_MS = 450;
function isDeliberateInput(event, sinceMs) {
  if (Date.now() - sinceMs < ARRIVAL_GRACE_MS) return false;
  if (event && event.type === 'wheel' && Math.abs(event.deltaX || 0) >= Math.abs(event.deltaY || 0)) return false;
  return true;
}

function readSnapshot(storageKey, flowPrefixes) {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const snap = JSON.parse(raw);
    if (!snap || Date.now() - (snap.timestamp || 0) > MAX_AGE_MS) return null;
    // Same page load only: a reload restarts the navigation count, which
    // could otherwise line up with an old snapshot by coincidence.
    if (snap.loadId !== LOAD_ID) return null;
    // Must actually have come back from somewhere in the flow, and never
    // left the flow since the tap: a drill-down chain restores (Offers ->
    // Offer History -> an offer -> back -> back), a detour elsewhere doesn't.
    if (!cameFrom(flowPrefixes)) return null;
    if (getNavIndex() <= snap.navIndex) return null;
    if (!stayedWithin(snap.navIndex, flowPrefixes)) return null;
    return snap;
  } catch (err) {
    return null;
  }
}

function findAnchor(anchorId) {
  if (!anchorId) return null;
  const nodes = document.querySelectorAll('[data-restore-anchor]');
  for (const node of nodes) {
    if (node.getAttribute('data-restore-anchor') === anchorId) return node;
  }
  return null;
}

/** The first anchored item whose bottom is still on screen, and its offset. */
function captureAnchor() {
  const nodes = document.querySelectorAll('[data-restore-anchor]');
  for (const node of nodes) {
    const rect = node.getBoundingClientRect();
    if (rect.bottom > 0) {
      return { anchorId: node.getAttribute('data-restore-anchor'), anchorTop: rect.top };
    }
  }
  return { anchorId: null, anchorTop: 0 };
}

export function useReturnViewState({ storageKey, flowPrefixes }) {
  const location = useLocation();
  const [snapshot] = useState(() => readSnapshot(storageKey, flowPrefixes));
  const skipScroll = location.state?.skipViewRestore === true;

  useEffect(() => {
    try { sessionStorage.removeItem(storageKey); } catch (err) { /* fine */ }
    // Consume the skip flag from the browser's history entry, so a LATER
    // back-navigation to this same entry (after tapping into an item again)
    // isn't also told to skip. React Router keeps navigate() state in
    // history.state.usr; the in-memory location is already read above.
    if (skipScroll) {
      try {
        const entry = window.history.state;
        if (entry && entry.usr && entry.usr.skipViewRestore) {
          const { skipViewRestore, ...rest } = entry.usr;
          window.history.replaceState(
            { ...entry, usr: Object.keys(rest).length ? rest : null },
            ''
          );
        }
      } catch (err) { /* fine */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const saveView = useCallback((tab) => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        tab,
        scrollY: window.scrollY,
        ...captureAnchor(),
        navIndex: getNavIndex(),
        loadId: LOAD_ID,
        timestamp: Date.now(),
      }));
    } catch (err) { /* storage unavailable: nothing to restore, harmless */ }
  }, [storageKey]);

  return {
    savedTab: snapshot?.tab || null,
    // What the scroll restore should use; null when there is nothing to do.
    scrollSnapshot: snapshot && !skipScroll ? snapshot : null,
    saveView,
  };
}

/**
 * Runs the scroll restore once `ready` turns true. Separate from
 * useReturnViewState so a page can call it after declaring the state that
 * `ready` is computed from.
 */
export function useRestoreScroll(scrollSnapshot, ready, { onStart } = {}) {
  const doneRef = useRef(false);
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  // Set if the user scrolls, taps, or types BEFORE the lists are ready (slow
  // connection): by then they've chosen where to be, so the restore stands
  // down instead of pulling the page away from them.
  const takenOverRef = useRef(false);

  // Detaches the early watcher; also called as soon as the restore starts or
  // the user takes over, so it doesn't keep running on every scroll after.
  const detachEarlyRef = useRef(null);

  useEffect(() => {
    if (!scrollSnapshot) return undefined;
    const mountedAt = Date.now();
    const detach = () => {
      USER_EVENTS.forEach((type) => window.removeEventListener(type, onEarlyInput, LISTENER_OPTS));
    };
    function onEarlyInput(event) {
      if (!isDeliberateInput(event, mountedAt)) return;
      takenOverRef.current = true;
      detach();
    }
    USER_EVENTS.forEach((type) => window.addEventListener(type, onEarlyInput, LISTENER_OPTS));
    detachEarlyRef.current = detach;
    return detach;
  }, [scrollSnapshot]);

  useEffect(() => {
    if (doneRef.current || !scrollSnapshot || !ready) return undefined;
    doneRef.current = true;
    if (detachEarlyRef.current) detachEarlyRef.current();
    if (takenOverRef.current) return undefined;
    // Lets the page cancel its own default (e.g. a scroll-to-top retry loop)
    // only once the restore really begins; if the lists never become ready,
    // that default stands and the user is left somewhere sensible.
    if (onStartRef.current) onStartRef.current();

    let stopped = false;
    let finished = false; // ran its course, or the user took over
    const timers = [];
    function stop() {
      stopped = true;
      timers.forEach(clearTimeout);
      USER_EVENTS.forEach((type) => window.removeEventListener(type, onUserInput, LISTENER_OPTS));
    }
    const startedAt = Date.now();
    function onUserInput(event) {
      if (!isDeliberateInput(event, startedAt)) return;
      finished = true;
      stop();
    }

    const apply = () => {
      if (stopped) return;
      const anchor = findAnchor(scrollSnapshot.anchorId);
      const y = anchor
        ? anchor.getBoundingClientRect().top + window.scrollY - (scrollSnapshot.anchorTop || 0)
        : (scrollSnapshot.scrollY || 0);
      window.scrollTo(0, Math.max(0, y));
    };

    USER_EVENTS.forEach((type) => window.addEventListener(type, onUserInput, LISTENER_OPTS));
    REASSERT_DELAYS_MS.forEach((delay) => {
      timers.push(setTimeout(() => requestAnimationFrame(apply), delay));
    });
    timers.push(setTimeout(() => { finished = true; stop(); }, REASSERT_DELAYS_MS[REASSERT_DELAYS_MS.length - 1] + 50));
    return () => {
      stop();
      // Dev StrictMode unmounts and remounts once to test cleanup: an
      // interrupted restore must be allowed to run again on the remount.
      if (!finished) doneRef.current = false;
    };
  }, [scrollSnapshot, ready]);
}

